/**
 * Clean Organics — tier-based bonus items + unlock animation on cart add.
 */
(function () {
  const cfg = window.CoCartBonus || {};
  const EBOOK_ID = String(cfg.ebookVariantId || 48438745628930);
  const DIET_ID = String(cfg.dietPlanVariantId || 48438745727234);
  const BONUS_IDS = [EBOOK_ID, DIET_ID];
  const MAIN_VARIANT_IDS = (cfg.mainVariantIds || ['48342027403522']).map(String);
  const MAIN_PRODUCT_ID = cfg.mainProductId ? String(cfg.mainProductId) : null;

  if (!BONUS_IDS.length) return;

  let busy = false;
  let skipNextCartSignal = false;

  function tierFromQuantity(qty) {
    if (qty >= 5) return 'tier3';
    if (qty >= 3) return 'tier2';
    if (qty >= 1) return 'tier1';
    return null;
  }

  function requiredBonusIds(tier) {
    if (tier === 'tier3' || tier === 'tier2') return [EBOOK_ID, DIET_ID];
    if (tier === 'tier1') return [EBOOK_ID];
    return [];
  }

  function isBonusItem(item) {
    return BONUS_IDS.includes(String(item.variant_id));
  }

  function isMainItem(item) {
    const variantId = String(item.variant_id);
    if (BONUS_IDS.includes(variantId)) return false;
    if (MAIN_VARIANT_IDS.includes(variantId)) return true;
    if (MAIN_PRODUCT_ID && String(item.product_id) === MAIN_PRODUCT_ID) return true;
    return false;
  }

  function detectTier(cart) {
    const mainQty = (cart?.items || []).reduce((sum, item) => {
      return isMainItem(item) ? sum + item.quantity : sum;
    }, 0);
    return tierFromQuantity(mainQty);
  }

  function idsInCart(cart) {
    return new Set((cart?.items || []).map((item) => String(item.variant_id)));
  }

  function missingBonusIds(required, cart) {
    const ids = idsInCart(cart);
    return required.filter((id) => !ids.has(id));
  }

  function extraBonusIds(required, cart) {
    const ids = idsInCart(cart);
    return BONUS_IDS.filter((id) => ids.has(id) && !required.includes(id));
  }

  function presentBonusIds(cart) {
    const ids = idsInCart(cart);
    return BONUS_IDS.filter((id) => ids.has(id));
  }

  function fetchCart() {
    return fetch(`${window.routes?.cart_url || '/cart'}.json`).then((r) => r.json());
  }

  function runWithSkipSignal(request) {
    skipNextCartSignal = true;
    return request().finally(() => {
      setTimeout(() => {
        skipNextCartSignal = false;
      }, 400);
    });
  }

  function addBonuses(missing) {
    if (!missing.length) return Promise.resolve(null);

    return runWithSkipSignal(() =>
      fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: missing.map((id) => ({
            id: Number(id),
            quantity: 1,
            properties: {
              Bonus: 'FREE with your order',
              Delivery: 'Email after checkout',
            },
          })),
        }),
      }).then((r) => r.json()),
    );
  }

  function removeBonusIds(idsToRemove, cart) {
    const removeSet = new Set(idsToRemove.map(String));
    const updates = {};

    (cart?.items || []).forEach((item) => {
      if (removeSet.has(String(item.variant_id))) {
        updates[item.variant_id] = 0;
      }
    });

    if (!Object.keys(updates).length) return Promise.resolve(null);

    return runWithSkipSignal(() =>
      fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      }).then((r) => r.json()),
    );
  }

  function updateShippingAttribute(tier) {
    const attributes =
      tier === 'tier3'
        ? { 'Free Shipping': 'Unlocked with your bundle' }
        : { 'Free Shipping': '' };

    return runWithSkipSignal(() =>
      fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      }).then((r) => r.json()),
    );
  }

  function refreshCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return Promise.resolve();

    return fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
          const target = document.querySelector(selector);
          const source = parsed.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });

        const isEmpty = parsed.querySelector('cart-drawer')?.classList.contains('is-empty');
        drawer.classList.toggle('is-empty', !!isEmpty);
      })
      .catch(() => null);
  }

  function refreshMainCart() {
    const mainItems = document.getElementById('main-cart-items');
    if (!mainItems?.dataset.id) return Promise.resolve();

    return fetch(`${window.routes?.cart_url || '/cart'}?section_id=${mainItems.dataset.id}`)
      .then((r) => r.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector('#main-cart-items .js-contents');
        const target = document.querySelector('#main-cart-items .js-contents');
        if (source && target) target.innerHTML = source.innerHTML;

        const footer = document.getElementById('main-cart-footer');
        const sourceFooter = parsed.querySelector('#main-cart-footer .js-contents');
        const targetFooter = document.querySelector('#main-cart-footer .js-contents');
        if (footer && sourceFooter && targetFooter) targetFooter.innerHTML = sourceFooter.innerHTML;

        const icon = parsed.querySelector('#cart-icon-bubble');
        const iconTarget = document.getElementById('cart-icon-bubble');
        if (icon && iconTarget) iconTarget.innerHTML = icon.innerHTML;
      })
      .catch(() => null);
  }

  function refreshCartUI() {
    return Promise.all([refreshCartDrawer(), refreshMainCart()]);
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    if (window.CoCart?.openNativeCart) window.CoCart.openNativeCart();
  }

  function spawnConfetti(container) {
    if (!container) return;
    container.innerHTML = '';
    const colors = ['#832626', '#963f3f', '#963f3f', '#5c1b1b', '#fdf6f6'];
    for (let i = 0; i < 24; i += 1) {
      const piece = document.createElement('span');
      piece.style.left = `${Math.random() * 100}%`;
      piece.style.background = colors[i % colors.length];
      piece.style.animationDelay = `${Math.random() * 0.4}s`;
      piece.style.animationDuration = `${1 + Math.random() * 0.6}s`;
      container.appendChild(piece);
    }
  }

  function showUnlockOverlay(tier) {
    const overlay = document.getElementById('CoCartBonusOverlay');
    if (!overlay) return;

    const gifts = window.CoBundleTier?.giftsForTier(tier) || ['ebook', 'diet'];
    overlay.querySelectorAll('[data-co-overlay-gift]').forEach((el) => {
      el.hidden = !gifts.includes(el.dataset.coOverlayGift);
    });

    const count = gifts.length;
    const subtitle = overlay.querySelector('[data-co-overlay-subtitle]');
    if (subtitle) {
      subtitle.innerHTML =
        count === 1
          ? "You've unlocked <strong>1 FREE bonus</strong> — automatically added to your cart."
          : `You've unlocked <strong>${count} FREE bonuses</strong> — automatically added to your cart.`;
    }

    spawnConfetti(overlay.querySelector('.co-bonus-overlay__confetti'));
    overlay.classList.add('is-active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    const close = () => {
      overlay.classList.remove('is-active');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      openCartDrawer();
    };

    overlay.querySelector('[data-co-bonus-close]')?.addEventListener('click', close, { once: true });
    overlay.addEventListener(
      'click',
      (e) => {
        if (e.target === overlay) close();
      },
      { once: true },
    );
  }

  async function syncBonuses({ showAnimation = false } = {}) {
    if (busy) return;
    busy = true;

    try {
      let cart = await fetchCart();
      const tier = detectTier(cart);

      if (!tier) {
        const present = presentBonusIds(cart);
        if (present.length) {
          await removeBonusIds(present, cart);
          await updateShippingAttribute(null);
          await refreshCartUI();
        }
        return;
      }

      const required = requiredBonusIds(tier);
      const extra = extraBonusIds(required, cart);
      let missing = missingBonusIds(required, cart);
      let didChange = false;

      if (extra.length) {
        await removeBonusIds(extra, cart);
        cart = await fetchCart();
        missing = missingBonusIds(required, cart);
        didChange = true;
      }

      if (missing.length) {
        const addResult = await addBonuses(missing);
        if (addResult?.status) return;
        didChange = true;
        await refreshCartUI();
        cart = await fetchCart();

        if (showAnimation) showUnlockOverlay(tier);
        else openCartDrawer();
      } else if (didChange) {
        await refreshCartUI();
      }

      await updateShippingAttribute(tier);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'co-cart-bonus', cartData: cart });
      }
    } catch (e) {
      console.error('[CoCartBonus]', e);
    } finally {
      busy = false;
    }
  }

  function onCartAddSignal() {
    if (skipNextCartSignal) return;
    setTimeout(() => syncBonuses({ showAnimation: true }), 120);
  }

  function onCartChangeSignal() {
    if (skipNextCartSignal) return;
    setTimeout(() => syncBonuses({ showAnimation: false }), 120);
  }

  function patchFetch() {
    if (window.__coCartBonusFetchPatched) return;
    window.__coCartBonusFetchPatched = true;

    const nativeFetch = window.fetch.bind(window);
    window.fetch = function patchedFetch(input, init) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const isCartAdd = /\/cart\/add(\.js)?/.test(url);
      const isCartChange = /\/cart\/(change|update)(\.js)?/.test(url);

      return nativeFetch(input, init).then((response) => {
        if (!response.ok) return response;
        if (isCartAdd) onCartAddSignal();
        else if (isCartChange) onCartChangeSignal();
        return response;
      });
    };
  }

  function init() {
    patchFetch();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || event.source === 'co-cart-bonus') return;
        if (event.source === 'product-form') onCartAddSignal();
        else onCartChangeSignal();
      });
    }

    document.addEventListener('cart:updated', onCartChangeSignal);
    document.addEventListener('kaching:cart:updated', onCartAddSignal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.CoCartBonusApi = { syncBonuses, showUnlockOverlay, detectTier, requiredBonusIds };
})();
